import React, {FC, useEffect, useRef, useState} from 'react'
import {Link, useLocation, useNavigate} from 'react-router-dom'
import {PageTitle} from '../../../_metronic/layout/core'
import {ChannelLinkPreview} from './core/_models'
import {confirmChannelLink, previewChannelLink, requestErrorMessage} from './core/_requests'
import './Channels.css'

type Status = 'loading' | 'ready' | 'invalid' | 'connecting' | 'done' | 'error'

// O código chega no fragmento (#code=...): não vai para log de servidor nem para Referer.
const readCode = (hash: string): string => {
  const match = /(?:^#|&)code=([0-9a-f]{32})/i.exec(hash)
  return match ? match[1] : ''
}

const ConnectDiscordPage: FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [code, setCode] = useState('')
  const [status, setStatus] = useState<Status>('loading')
  const [preview, setPreview] = useState<ChannelLinkPreview | null>(null)
  const [message, setMessage] = useState('')
  // Um clique num link do Discord muda só o fragmento (#code=...): se a aba já estava aberta em
  // /connect/discord (de uma tentativa anterior), o navegador não recarrega a página, só atualiza
  // location.hash. Por isso o efeito depende de location.hash, não roda só uma vez, e usa esta ref
  // para não reprocessar o mesmo código nem "invalidar" o fragmento que a própria página já limpou.
  const processedCode = useRef<string | null>(null)

  useEffect(() => {
    const found = readCode(location.hash)

    if (found && found !== processedCode.current) {
      processedCode.current = found
      setCode(found)
      setStatus('loading')
      setMessage('')
      // Tira o código da barra de endereço e do histórico assim que ele foi lido.
      navigate('/connect/discord', {replace: true})

      previewChannelLink(found)
        .then((data) => {
          setPreview(data)
          setStatus('ready')
        })
        .catch((error) => {
          setMessage(requestErrorMessage(error, 'Este código expirou ou já foi usado.'))
          setStatus('invalid')
        })
      return
    }

    if (!found && processedCode.current === null) {
      setStatus('invalid')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash])

  const connect = () => {
    setStatus('connecting')
    confirmChannelLink(code)
      .then(() => setStatus('done'))
      .catch((error) => {
        setMessage(requestErrorMessage(error, 'Não foi possível conectar agora. Tente de novo.'))
        setStatus('error')
      })
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>Conectar o Discord</PageTitle>

      <div className='dash-section channels-connect'>
        {status === 'loading' && <p className='text-muted mb-0'>Verificando o código...</p>}

        {status === 'invalid' && (
          <div data-testid='connect-invalid'>
            <h3 className='dash-section-title mb-3'>Não deu para conectar</h3>
            <p className='mb-3'>
              {message ||
                'Este link não é válido, expirou ou já foi usado. Mande uma mensagem ao RushMind no Discord para receber um novo.'}
            </p>
            <Link to='/dashboard' className='btn btn-light'>
              Voltar ao TaskRush
            </Link>
          </div>
        )}

        {(status === 'ready' || status === 'connecting' || status === 'error') && preview && (
          <div data-testid='connect-ready'>
            <h3 className='dash-section-title mb-3'>Conectar sua conta do Discord</h3>

            <div className='channels-account mb-5'>
              {preview.avatar_url ? (
                <img className='channels-account-avatar' src={preview.avatar_url} alt='' />
              ) : (
                <span className='channels-account-avatar' aria-hidden='true' />
              )}
              <div>
                <div className='fw-bold fs-5'>{preview.display_name}</div>
                <div className='channels-account-id'>ID no Discord: {preview.external_user_id}</div>
              </div>
            </div>

            <p className='fw-semibold mb-2'>Depois de conectar, o RushMind poderá, em seu nome:</p>
            <ul className='channels-can-list mb-5'>
              <li>consultar suas tarefas, projetos, prazos e marcos;</li>
              <li>criar tarefas (sempre pedindo a sua confirmação antes);</li>
              <li>nunca apagar nada.</li>
            </ul>

            <div className='channels-warning mb-5'>
              <strong>Só conecte se foi você que pediu isso no Discord.</strong> Se não reconhece essa
              conta, feche esta página.
            </div>

            {status === 'error' && (
              <div className='alert alert-danger' role='alert'>
                {message}
              </div>
            )}

            <button
              type='button'
              className='btn btn-primary'
              onClick={connect}
              disabled={status === 'connecting'}
            >
              {status === 'connecting' ? 'Conectando...' : 'Conectar'}
            </button>
          </div>
        )}

        {status === 'done' && (
          <div data-testid='connect-done'>
            <h3 className='dash-section-title mb-3'>Conta conectada</h3>
            <p className='mb-4'>
              Pronto! Volte ao Discord e mande uma mensagem ao RushMind, por exemplo: "o que está
              atrasado para mim?".
            </p>
            <div className='d-flex gap-3 flex-wrap'>
              <Link to='/meus-canais' className='btn btn-primary'>
                Ver meus canais
              </Link>
              <Link to='/dashboard' className='btn btn-light'>
                Ir para o TaskRush
              </Link>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export {ConnectDiscordPage}
